import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  Avatar,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VideocamIcon from '@mui/icons-material/Videocam';

const AddPraticienDialog = ({
  open,
  onClose,
  praticienData,
  setPraticienData,
  praticienPreview,
  onFileChange,
  onSubmit,
  isSubmitting,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPraticienData((prev) => ({
      ...prev,
      // Checkbox => vrai booléen (true / false), sinon la valeur telle quelle
      [name]: type === 'checkbox' ? Boolean(checked) : value,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {/* En-tête */}
      <DialogTitle sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F8F9FA' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#2B3674', letterSpacing: '-0.5px' }}>
            Ajouter un Praticien
          </Typography>
          <Typography variant="body2" sx={{ color: '#A3AED0', mt: 0.5 }}>
            Remplissez les informations ci-dessous pour créer un nouveau compte praticien.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ bgcolor: '#fff', boxShadow: '0px 2px 6px rgba(0,0,0,0.05)', '&:hover': { bgcolor: '#F4F7FE' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Contenu du formulaire */}
      <DialogContent dividers sx={{ p: 4, maxHeight: '70vh', bgcolor: '#FFFFFF' }}>
        <Stack spacing={3} sx={{ width: '100%' }}>

          {/* Photo de profil */}
          <Box display="flex" alignItems="center" gap={3} sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '16px', width: '100%' }}>
            <Avatar
              src={praticienPreview}
              sx={{ width: 80, height: 80, bgcolor: '#E0E5F2', color: '#4318FF', fontWeight: 700, border: '2px solid #fff', boxShadow: '0px 4px 10px rgba(0,0,0,0.05)' }}
            >
              {!praticienPreview && 'PHOTO'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2B3674', mb: 1 }}>
                Photo du praticien
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                size="small"
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  borderColor: '#4318FF',
                  color: '#4318FF',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#F4F7FE', borderColor: '#3311CC' },
                }}
              >
                Télécharger une image
                <input type="file" hidden accept="image/*" onChange={onFileChange} />
              </Button>
            </Box>
          </Box>

          {/* Informations personnelles & professionnelles */}
          <Box sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PersonOutlineIcon sx={{ color: '#4318FF' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B3674' }}>
                Informations personnelles & Professionnelles
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Prénom"
                  name="prenom_user"
                  value={praticienData.prenom_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Nom"
                  name="nom_user"
                  value={praticienData.nom_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Box>

              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email_user"
                  type="email"
                  value={praticienData.email_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Mot de passe"
                  name="password_user"
                  type={showPassword ? 'text' : 'password'}
                  value={praticienData.password_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="changer la visibilité du mot de passe"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  name="phone_user"
                  value={praticienData.phone_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Titre / Fonction"
                  name="title_user"
                  value={praticienData.title_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Box>

              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Spécialité principale"
                  name="main_speciality_user"
                  value={praticienData.main_speciality_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  select
                  fullWidth
                  label="Statut du compte"
                  name="etat_user"
                  value={praticienData.etat_user || 'active'}
                  onChange={handleChange}
                  size="small"
                >
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="inactive">Inactif</MenuItem>
                </TextField>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description_user"
                value={praticienData.description_user || ''}
                onChange={handleChange}
                required
                size="small"
              />
            </Stack>
          </Box>

          <Divider sx={{ borderColor: '#E0E5F2' }} />

          {/* Localisation */}
          <Box sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationOnOutlinedIcon sx={{ color: '#4318FF' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B3674' }}>
                Adresse & Localisation
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Adresse"
                name="addresse_user"
                value={praticienData.addresse_user || ''}
                onChange={handleChange}
                required
                size="small"
              />

              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Ville"
                  name="ville_user"
                  value={praticienData.ville_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Code Postal"
                  name="cp_user"
                  value={praticienData.cp_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Province"
                  name="province_user"
                  value={praticienData.province_user || ''}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: '#E0E5F2' }} />

          {/* Paramètres financiers & commissions */}
          <Box sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PaymentsOutlinedIcon sx={{ color: '#4318FF' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B3674' }}>
                Paramètres Financiers & Commissions
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Prix SOA"
                name="prix_soa"
                type="number"
                value={praticienData.prix_soa ?? ''}
                onChange={handleChange}
                size="small"
              />

              <Box display="flex" gap={2} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Comm. Consultation (%)"
                  name="commission_consultation"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 'any' }}
                  value={praticienData.commission_consultation ?? ''}
                  onChange={handleChange}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Comm. Vidéo (%)"
                  name="commission_video"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 'any' }}
                  value={praticienData.commission_video ?? ''}
                  onChange={handleChange}
                  size="small"
                />
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: '#E0E5F2' }} />

          {/* Visioconférence (booléen) */}
          <Box sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <VideocamIcon sx={{ color: '#4318FF' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2B3674' }}>
                Paramètres de visioconférence
              </Typography>
            </Box>

            <Box sx={{ p: 2, bgcolor: '#F4F7FE', borderRadius: '16px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(praticienData.create_zoom_account)}
                    onChange={handleChange}
                    name="create_zoom_account"
                    sx={{ color: '#4318FF', '&.Mui-checked': { color: '#4318FF' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>
                    Créer automatiquement un compte Zoom pour ce praticien
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, bgcolor: '#F8F9FA' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            borderColor: '#E0E5F2',
            color: '#2B3674',
            px: 3,
            '&:hover': { bgcolor: '#F4F7FE', borderColor: '#C4CFEE' },
          }}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#4318FF',
            borderRadius: '12px',
            textTransform: 'none',
            px: 4,
            boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.3)',
            '&:hover': { bgcolor: '#3311CC', boxShadow: '0px 6px 16px rgba(67, 24, 255, 0.4)' },
          }}
        >
          {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Enregistrer le praticien'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPraticienDialog;